import clsx from "clsx";
import { Suspense, useRef } from "preact/compat";
import { Toaster } from "react-hot-toast";
import { useMetaTags } from "react-metatags-hook";
import { NavLink, Outlet, useLocation } from "react-router";
import logoGeneric from "@/assets/logos/generic.svg";
import SJLogo from "@/assets/logos/sj_logo.svg";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import DevModal from "@/components/shared/dev-modal";
import { scheduleInfo } from "@/data/information";
import useScrollRestoration from "@/hooks/useScrollRestoration";
import { getAssetImageUrl } from "@/util/common.js";
import { defaultOpenGraphImage, isTestMode } from "@/util/const.js";

export default function SchedulePageLayout() {
  const location = useLocation();

  const { title, name, description, shortDescription, images } = scheduleInfo;

  let metaImage;

  if (images && images.length > 0) {
    metaImage = getAssetImageUrl(images[0]);
  } else {
    metaImage = defaultOpenGraphImage;
  }

  useMetaTags(
    {
      title,
      description: description || shortDescription,
      openGraph: {
        image: metaImage,
        description: description || shortDescription,
      },
    },
    [name, description, shortDescription, metaImage],
  );
  const scrollRef = useRef(null);

  const childRef = useRef(null);
  useScrollRestoration({
    scrollRef: scrollRef,
    childRef: childRef,
    pathname: location.pathname,
  });

  function setScrollRef(div) {
    scrollRef.current = div;
  }

  function setChildRef(div) {
    childRef.current = div;
  }

  return (
    <>
      <Toaster>
        {(t) => (
          <div
            role="alert"
            className={`${
              t.visible ? "animate-enter" : "animate-leave"
            } alert max-w-screen-lg ${t.className}`}
          >
            {t.message}
          </div>
        )}
      </Toaster>
      <div className="flex flex-col w-full h-full overflow-hidden relative">
        <Header />
        <DevModal />
        <div
          className="scroll-ref w-full flex-grow flex flex-col overflow-y-auto overflow-x-hidden"
          ref={setScrollRef}
        >
          <div className="h-full flex flex-col scrollbar-gutter-fix">
            <div className="flex-grow" ref={setChildRef}>
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function Header() {
  const logoInner = (
    <>
      <img alt="St. Jude Logo" src={SJLogo} className={!isTestMode ? "w-10" : "hidden"} />
      <img
        alt="Play Live Tools Logo"
        src={`${logoGeneric}`}
        className={isTestMode ? "w-8 fill-gray-600 opacity-50" : "hidden"}
      />
      <span className={!isTestMode ? "" : "hidden"}>St. Jude</span>
      <span className={"text-primary-content"}>Play Live Schedule</span>
    </>
  );

  return (
    <div className={clsx(["navbar bg-primary text-primary-content items-center flex-col"])}>
      <div className="flex-grow md:justify-between transition-all flex items-center w-full">
        <div className="flex-none md:flex-1 hidden md:block">
          <NavLink
            unstable_viewTransition
            className="lg:btn-ghost no-animation text-xl hidden lg:btn"
            to="/"
          >
            {logoInner}
          </NavLink>
        </div>

        <div className="flex-2 md:hidden">
          <NavLink
            unstable_viewTransition
            to="/"
            className="text-lg pr-5 no-animation w-full btn btn-ghost"
          >
            {logoInner}
          </NavLink>
        </div>
        <div className="flex-none"></div>
        <ThemeToggle />
      </div>
    </div>
  );
}
